import { MapPin, Clock, Phone, Mail, Truck, Heart } from "lucide-react";
import EmailLink from "@/components/EmailLink";
import Seo from "@/components/Seo";

export default function Atelier() {
  return (
    <div className="page-transition pt-[72px]" data-testid="atelier-page">
      <Seo
        title="About Fleuris Noir — Minneapolis Luxury Florist"
        description="Fleuris Noir is a luxury florist serving Minneapolis and the Twin Cities since 2018, crafting premium rose bouquets, bespoke arrangements and event florals."
        path="/atelier"
      />
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1760373071711-960143464e34?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBmbG9yaXN0JTIwc2hvcHxlbnwwfHx8fDE3NzYyNjIwMzl8MA&ixlib=rb-4.1.0&q=85')` 
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        
        <div className="relative z-10 text-center px-6">
          <p className="overline mb-4">Our Story</p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#f8f5f0]">
            About Fleuris Noir
          </h1>
        </div>
      </section>

      {/* Story */}
      <section className="section bg-[#0c0c0c]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="overline mb-4">Founded 2018</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#f8f5f0] mb-6">
                Where Darkness Meets Botanical Beauty
              </h2>
              <div className="space-y-4 font-body text-[#a19d98] leading-relaxed">
                <p>
                  Fleuris Noir was born from a desire to challenge the conventional. 
                  We believe that beauty thrives in contrast — that the most captivating 
                  arrangements emerge when delicate petals are set against shadow and depth.
                </p>
                <p>
                  As a fully mobile company, we bring the art of floristry directly to you. 
                  Whether it's a stunning bouquet delivered to your door, a custom 
                  arrangement for your special occasion, or our signature Floral Bar 
                  pop-up at your event — we go wherever beauty is needed.
                </p>
                <p>
                  Based in Minneapolis, we serve the greater metro area and beyond. 
                  Our founder was inspired by the moody aesthetics of Northern European design 
                  and the rich floral traditions of classic botany, creating a brand where 
                  floristry becomes an art form that speaks to the sophisticated and the bold.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1647644520784-2a4bd03a4204?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbW9vZHklMjBmbG9yYWwlMjBhcnJhbmdlbWVudHxlbnwwfHx8fDE3NzYyNjIwMzl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Fleuris Noir Arrangement"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 border border-[#b8966a]/30" />
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="section bg-[#161616]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="overline mb-4">What Sets Us Apart</p>
            <h2 className="font-heading text-3xl text-[#f8f5f0]">
              The Fleuris Noir Difference
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                icon: <Truck className="text-[#b8966a]" size={24} />,
                title: "Fully Mobile",
                desc: "We come to you. Delivery, events, and our signature Floral Bar pop-up — all at your location."
              },
              {
                icon: <Heart className="text-[#b8966a]" size={24} />,
                title: "Dark Aesthetic",
                desc: "Our signature moody style sets us apart. Every arrangement is designed with depth, contrast, and emotion."
              },
              {
                icon: <MapPin className="text-[#b8966a]" size={24} />,
                title: "Minneapolis Roots",
                desc: "Proudly serving the Twin Cities and beyond. Local sourcing meets world-class design."
              }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 border border-[#b8966a]/20 flex items-center justify-center">
                  {item.icon}
                </div>
                <h3 className="font-heading text-lg text-[#f8f5f0] mb-2">{item.title}</h3>
                <p className="font-body text-sm text-[#a19d98]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section bg-[#0c0c0c] border-y border-[#b8966a]/10">
        <div className="container-custom max-w-3xl text-center">
          <p className="overline mb-4">Our Philosophy</p>
          <p className="font-heading text-2xl md:text-3xl text-[#f8f5f0] leading-relaxed italic mb-8">
            "We don't simply arrange flowers — we compose visual poetry. Each stem is selected 
            not merely for its beauty, but for the emotion it evokes, the memory it creates, 
            the story it tells."
          </p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="section bg-[#161616]">
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-12">
            <p className="overline mb-4">Get In Touch</p>
            <h2 className="font-heading text-3xl text-[#f8f5f0]">
              Reach Out To Us
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <MapPin className="text-[#b8966a] mx-auto mb-3" size={24} />
              <h3 className="font-heading text-lg text-[#f8f5f0] mb-2">Based In</h3>
              <p className="font-body text-[#a19d98]">Minneapolis, MN</p>
              <p className="font-body text-sm text-[#a19d98] mt-1">Serving the greater metro & beyond</p>
            </div>

            <div className="text-center">
              <Phone className="text-[#b8966a] mx-auto mb-3" size={24} />
              <h3 className="font-heading text-lg text-[#f8f5f0] mb-2">Call Us</h3>
              <a href="tel:+16125479260" className="font-body text-[#a19d98] hover:text-[#b8966a] transition-colors">
                (612) 547-9260
              </a>
            </div>

            <div className="text-center">
              <Mail className="text-[#b8966a] mx-auto mb-3" size={24} />
              <h3 className="font-heading text-lg text-[#f8f5f0] mb-2">Email</h3>
              <EmailLink
                email="hello@fleurisnoir.com"
                className="font-body text-[#a19d98] hover:text-[#b8966a] transition-colors"
                testId="atelier-email"
              >
                hello@fleurisnoir.com
              </EmailLink>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
